import type { MotionValue, MotionProps } from 'framer-motion';

import { twMerge } from 'tailwind-merge';
import { m, useSpring, useTransform } from 'framer-motion';

import { scrollProgressClasses } from '@/core/constants';

// ----------------------------------------------------------------------

type BaseProps = MotionProps & React.ComponentProps<'svg'> & React.ComponentProps<'div'>;

export interface ScrollProgressProps extends BaseProps {
  size?: number;
  portal?: boolean;
  thickness?: number;
  whenScroll?: 'x' | 'y';
  className?: string;
  progress: MotionValue<number>;
  variant: 'linear' | 'circular';
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' | 'inherit' | string;
  slotProps?: {
    wrapper?: React.HTMLAttributes<HTMLDivElement>;
  };
}

export function ScrollProgress({
  size,
  portal,
  variant,
  slotProps,
  className,
  progress,
  thickness = 3.6,
  whenScroll = 'y',
  color = 'primary',
  ...other
}: ScrollProgressProps) {
  // Assume LTR direction by default
  const isRtl = false;

  const transformProgress = useTransform(progress, [0, -1], [0, 1]);

  const progressValue = isRtl && whenScroll === 'x' ? transformProgress : progress;
  const progressSize = variant === 'circular' ? (size ?? 64) : (size ?? 3);

  const scaleX = useSpring(progressValue, { stiffness: 100, damping: 30, restDelta: 0.001 });

  // Map color to Tailwind classes
  const getColorClasses = () => {
    if (color === 'inherit') return 'text-current';

    const colorMap: Record<string, string> = {
      primary: 'text-primary',
      secondary: 'text-gray-600',
      success: 'text-green-600',
      error: 'text-red-600',
      info: 'text-sky-600',
      warning: 'text-amber-600',
    };

    return colorMap[color] || 'text-primary';
  };

  // Map color to gradient background for linear variant
  const getGradientClasses = () => {
    if (color === 'inherit') return 'bg-current';

    const gradientMap: Record<string, string> = {
      primary: 'bg-gradient-to-r from-primary/50 to-primary',
      secondary: 'bg-gradient-to-r from-gray-500 to-gray-600',
      success: 'bg-gradient-to-r from-green-500 to-green-600',
      error: 'bg-gradient-to-r from-red-500 to-red-600',
      info: 'bg-gradient-to-r from-sky-500 to-sky-600',
      warning: 'bg-gradient-to-r from-amber-500 to-amber-600',
    };

    return gradientMap[color] || 'bg-gradient-to-r from-primary/50 to-primary';
  };

  const renderCircular = () => (
    <m.svg
      viewBox={`0 0 ${progressSize} ${progressSize}`}
      xmlns="http://www.w3.org/2000/svg"
      className={twMerge(
        scrollProgressClasses.circular,
        getColorClasses(),
        'rotate-[-90deg]',
        className
      )}
      style={{
        width: progressSize,
        height: progressSize,
      }}
      {...other}
    >
      <circle
        cx={progressSize / 2}
        cy={progressSize / 2}
        r={progressSize / 2 - thickness - 4}
        strokeWidth={thickness}
        strokeOpacity={0.2}
        fill="none"
        stroke="currentColor"
      />

      <m.circle
        cx={progressSize / 2}
        cy={progressSize / 2}
        r={progressSize / 2 - thickness - 4}
        strokeWidth={thickness}
        style={{ pathLength: progressValue }}
        fill="none"
        stroke="currentColor"
        strokeDashoffset={0}
      />
    </m.svg>
  );

  const renderLinear = () => (
    <m.div
      className={twMerge(
        scrollProgressClasses.linear,
        getGradientClasses(),
        'top-0 left-0 right-0 origin-[0%]',
        className
      )}
      style={{
        scaleX,
        height: progressSize,
      }}
      {...other}
    />
  );

  // Use conditional rendering instead of Portal
  const content = (
    <div {...slotProps?.wrapper}>{variant === 'circular' ? renderCircular() : renderLinear()}</div>
  );

  return portal ? <div id="portal-root">{content}</div> : <>{content}</>;
}

// ----------------------------------------------------------------------
